import { memo, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

import { Device } from "../../../../../../API";
import { extractDeviceNameFromId } from "../../../../../../common/utils/getEntityName";
import { selectedStreamObjectVariable } from "../../../../../../common/variables/selectedStreamObject";
import mqttPublish from "../../../../../../common/helpers/mqttPublish";
import ClickToPlayHLSStream from "../cameraView/ClickToPlayHLSStream";
import LiveStreamPlayer from "../cameraView/LiveStreamPlayer";
import HLSStreamPlayer from "../cameraView/HLSStreamPlayer";
import PauseIcon from "@mui/icons-material/Pause";
import LiveViewDialog from "../dialog/LiveViewDialog";
import useLocationFromCache from "../../../../../../common/hooks/useLocationFromCache";

export enum LiveViewMode {
  Live = "live",
  Recordings = "recordings",
}

interface LiveViewSidebarPlayerProps {
  device: Device;
  mode?: LiveViewMode;
  showControls?: boolean;
  /**
   * Optional HLS URL for VOD/Timelapse playback. When provided,
   * the component renders a plain HLS player instead of live controls.
   */
  hlsUrl?: string | null;
}

const LiveViewSidebarPlayer = ({
  device,
  hlsUrl,
  mode = LiveViewMode.Live,
  showControls = false,
}: LiveViewSidebarPlayerProps) => {
  const { kinesisStreamMessage } = mqttPublish();

  const [videoLoading, setVideoLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | undefined>();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerHeight = isFullscreen ? "100%" : "280px";

  const { getCachedLocationName } = useLocationFromCache();

  useEffect(() => {
    // No teardown required for VOD playback
    if (hlsUrl) return;

    return () => {
      if (!isStreaming) return;

      const { extractedDeviceName } = extractDeviceNameFromId(device.id);

      kinesisStreamMessage({
        action: "END",
        deviceName: extractedDeviceName,
        deviceId: device.id,
        nodeId: device.nodeId,
      }).finally(() => {
        selectedStreamObjectVariable(null);
      });
    };
  }, [isStreaming, hlsUrl]);

  const handleStarted = (): void => {
    setIsStreaming(true);
  };

  const handleStopped = (): void => {
    setIsStreaming(false);
  };

  const toggleFullscreen = (state: boolean): void => {
    if (state === false) {
      setIsStreaming(false);
    }

    setIsFullscreen(state);
  };

  if (hlsUrl) {
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "280px",
          backgroundColor: "#000",
          overflow: "hidden",
        }}
      >
        <LiveStreamPlayer
          streamUrl={hlsUrl}
          showControls={showControls}
          isLive={false}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: playerHeight,
        backgroundColor: "#000",
      }}
    >
      {!isStreaming && (
        <ClickToPlayHLSStream
          device={device}
          onStarted={handleStarted}
          fixedHeight={playerHeight}
          onAspectRatio={setAspectRatio}
        />
      )}

      {isStreaming && (
        <>
          <HLSStreamPlayer
            videoLoading={videoLoading}
            setVideoLoading={setVideoLoading}
            liveBadgePlacement="right"
            fixedHeight={playerHeight}
            providedAspectRatio={aspectRatio}
          />

          {!videoLoading && (
            <Box
              sx={{
                position: "absolute",
                top: "0%",
                left: "0%",
                right: "0%",
                bottom: "0%",

                "&:hover button": {
                  opacity: 1,
                },
              }}
            >
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  opacity: 0,

                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.6)",
                  },
                }}
                onClick={handleStopped}
                aria-label="Pause stream"
              >
                <PauseIcon sx={{ fontSize: 36 }} />
              </IconButton>
            </Box>
          )}
        </>
      )}

      {mode === LiveViewMode.Live && !videoLoading && (
        <IconButton
          sx={{
            position: "absolute",
            bottom: 12,
            right: 12,
            zIndex: 2,
            color: "white",
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
            },
          }}
          onClick={() => {
            toggleFullscreen(!isFullscreen);
          }}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          <FullscreenIcon sx={{ fontSize: 24 }} />
        </IconButton>
      )}

      {isFullscreen && (
        <LiveViewDialog
          setOpen={toggleFullscreen}
          deviceId={device.id}
          deviceName={device.name}
          locationName={getCachedLocationName(device.locationId)}
          nodeId={device.nodeId}
          hasPanTilt={device.deviceData.hasPanTilt}
          hasZoom={device.deviceData.hasZoom}
          deviceData={device.deviceData}
          currentDevice={device.id}
        />
      )}
    </Box>
  );
};

export default memo(LiveViewSidebarPlayer);
